import { Injectable } from '@angular/core';
import { LearnerHelperService } from './learner-helper.service';
import { NetwordDetectionService } from 'modules/app/services/network-detection.service';
import { UserdataService } from './userdata.service';
import { TeacherHelperService } from './teacher-helper.service';
import { HelperService } from './helper.service';
import { ActivateComponentService } from './activate-component.service';
import { ProductEntitlementsService } from './product-entitlements.service';
import { UmbrellaService } from './umbrella.service';
import { DesktopGlobalStateService } from './desktop-global-state.service';
import { EbookHelperService } from './ebook-helper.service';
import { DownloadHelperService } from './download-helper.service';
import { DesktopDatabaseHelperService } from './desktop-database-helper.service';
@Injectable()
export class ClassEnrollmentService {
    // stores the modeled / modified spaces -> classes -> bundles -> products
    spaceList;

    // var to check if user has no class but has atleast one space
    noUserClassButUserSpace;

    constructor(
        private networkService: NetwordDetectionService,
        private userdataService: UserdataService,
        private teacherHelperService: TeacherHelperService,
        private helperService: HelperService,
        private activateComponentService: ActivateComponentService,
        private umbrellaService: UmbrellaService,
        private desktopGlobalStateService: DesktopGlobalStateService,
        private ebookHelperService: EbookHelperService,
        private desktopDatabaseHelperService: DesktopDatabaseHelperService,
        private learnerHelperService: LearnerHelperService,
        private productEntitlementsService: ProductEntitlementsService,
        private downloadHelperService: DownloadHelperService,
    ) {

    }

    // function to fetch learner spaces and classes.
    // if online fetch data from server and store the fetched data in DB.
    // online/offline - fetch data from DB, transform and return.
    // returns teacher classes and Bundles
    public async fetchLearnerClasses() {

        let user = this.userdataService.getLoggedInUser();
        // if data is already present then return without new fetch.
        let userSpaces = await this.getUserSpacesData();
        if (userSpaces) {
            return userSpaces;
        }

        //Save latest data to DB if online
        if (await this.networkService.isOnline(true)) {
            let spacesData = await this.learnerHelperService.getLearnerSpacesAndClassData().toPromise();

            // Set this data in DB. Fetch from DB. Modify the data.
            // save learner spaces here.
            if (spacesData && spacesData.spaces) {
                let transformedData = this.transformSpacesData({ spaces: spacesData.spaces }, false);
                this.saveSpacesToDB(user, spacesData.spaces, true);
                return transformedData;
            }
        } else {
            //Get Data from DB
            let retrivedData = await this.fetchSavedSpacesFromDB(user);
            let transformedData = this.transformSpacesData(retrivedData, false);
            return transformedData;
        }


    }

    // function to fetch teacher spaces, classes, entitlements and bundles.
    // if online fetch data from server and store the fetched data in DB.
    // online/offline - fetch data from DB, transform and return.
    // returns teacher classes and Bundles
    public async fetchTeacherClassesAndBundles(fetchFromServer = false, dataNeeded) {

        let user = this.userdataService.getLoggedInUser();

        // if data is already present then return without new fetch.
        if (!fetchFromServer) {
            let result = {
                spaces: undefined,
                bundles: undefined,
                hasNoClassButSpaces: this.noUserClassButUserSpace,
                teacherSpacesCount: 0
            };
            if (dataNeeded.spaces || dataNeeded.classes) {
                result.spaces = await this.getUserSpacesData();
                result.teacherSpacesCount = result && result.spaces && result.spaces.entities && result.spaces.entities.length;

            }
            if (dataNeeded.bundles) {
                result.bundles = await this.umbrellaService.getTeacherBundlesData();
            }
            return result;
        }

        // check if spaces data was feteched from server
        let fetchedSpacesFromServerOnce = this.desktopGlobalStateService.getState({ propName: DesktopGlobalStateService.STATE_PROPS.SPACES });

        // check if bundles data was feteched from server
        let fetchedBundlesFromServerOnce = this.desktopGlobalStateService.getState({ propName: DesktopGlobalStateService.STATE_PROPS.BUNDLES });

        let fetchSpacesData = (dataNeeded.spaces || dataNeeded.classes) && !fetchedSpacesFromServerOnce;
        let fetchbundlesData = dataNeeded.bundles && !fetchedBundlesFromServerOnce;

        // fetch and save latest data to DB if online
        // If online, model the data to render the view, then set in DB
        // If offline, fetch from DB and use it, model it to render the view.
        if (await this.networkService.isOnline(true) && (fetchSpacesData || fetchbundlesData)) {
            let options = {};
            let filteredBundles;
            let transformedData: any = {};

            // Do not fetch institution request data as it is not reqired.
            // dataNeeded could be used as is without creating new object. Creating new object here for better visibility and debugging.
            let datatoBeFetched = {
                spaces: dataNeeded.spaces,
                classes: dataNeeded.classes,
                entitlements: dataNeeded.entitlements,
                bundles: dataNeeded.bundles,
                institutionRequest: dataNeeded.institutionRequest
            };


            let dataFetchedFromServer = await this.teacherHelperService.getSpacesAndClasses(datatoBeFetched, options).toPromise();

            //retrying if the data is not fetched in one call
            let dataFetchedFromServerCounter = 0;
            while (!dataFetchedFromServer && dataFetchedFromServerCounter < 3) {
                dataFetchedFromServerCounter++;
                dataFetchedFromServer = await this.teacherHelperService.getSpacesAndClasses(dataNeeded, options).toPromise();
            }

            if (dataFetchedFromServer) {

                if (dataFetchedFromServer.status == "error" && dataFetchedFromServer.error == "INVALID_USER_ROLE") {
                    // logout user if some non-teacher tried to access teacher data
                    return;
                }

                // Save teacher classes here.
                if (dataFetchedFromServer.entitlements) {
                    // setting the flag to signify that teachers entitlements have been fetched
                    this.desktopGlobalStateService.updateState(DesktopGlobalStateService.STATE_PROPS.ENTITLEMENTS, true);

                    this.modifyEntitlementsAndActivateProducts(dataFetchedFromServer.entitlements);

                    // set data in view-model but not in DB
                    this.productEntitlementsService.saveUserEntitlementsToDB(user, dataFetchedFromServer.entitlements, false);
                }

                // set bundles before spaces as these bundles are later used to find and use single bundles.
                if (dataFetchedFromServer.bundles) {
                    // setting the flag to signify that teachers bundles have been fetched
                    this.desktopGlobalStateService.updateState(DesktopGlobalStateService.STATE_PROPS.BUNDLES, true);

                    filteredBundles = this.umbrellaService.filterUmbrellasWithoutEbooks(dataFetchedFromServer.bundles);
                    transformedData.bundles = this.umbrellaService.transformUmbrellasData(filteredBundles);

                    // set data in view-model but not in DB
                    this.umbrellaService.filterAndSaveUmbrellasToDB(filteredBundles, false);
                }

                if (dataFetchedFromServer.spaces) {
                    // setting the flag to signify that teachers spaces have been fetched
                    this.desktopGlobalStateService.updateState(DesktopGlobalStateService.STATE_PROPS.SPACES, true);

                    transformedData.teacherSpacesCount = dataFetchedFromServer && dataFetchedFromServer.spaces && dataFetchedFromServer.spaces.entities && dataFetchedFromServer.spaces.entities.length;
                    transformedData.spaces = this.transformSpacesData({ spaces: dataFetchedFromServer.spaces }, true);
                    transformedData.hasNoClassButSpaces = this.noUserClassButUserSpace;

                    // set data in view-model but not in DB
                    this.saveSpacesToDB(user, dataFetchedFromServer.spaces, false);
                }

                // set data in view-model and in DB
                this.addFetchedDataToDB(user, dataFetchedFromServer.entitlements, dataFetchedFromServer.spaces, filteredBundles);

                return transformedData;
            } else {
                //TODO: Handling - if data is not fetched even after 3 calls
                console.log("spaces and bundles not fetched")
            }

        } else {

            //Get Data from DB
            let transformedData: any = {};

            if (dataNeeded.entitlements) {
                let savedEntitlements = await this.productEntitlementsService.fetchSavedEntitlementsFromDB(user);
                this.modifyEntitlementsAndActivateProducts(savedEntitlements.data.entities);
            }

            if (dataNeeded.bundles) {
                transformedData.bundles = await this.umbrellaService.fetchSavedUmbrellasFromDB();
                transformedData.bundles = this.umbrellaService.transformUmbrellasData(transformedData.bundles);
            }

            if (dataNeeded.spaces || dataNeeded.classes) {
                transformedData.spaces = await this.fetchSavedSpacesFromDB(user);
                transformedData.teacherSpacesCount = transformedData && transformedData.spaces && transformedData.spaces.spaces && transformedData.spaces.spaces.entities && transformedData.spaces.spaces.entities.length;
                transformedData.spaces = this.transformSpacesData(transformedData.spaces, true);
                transformedData.hasNoClassButSpaces = this.noUserClassButUserSpace;
            }

            return transformedData;
        }
    }

    public async addFetchedDataToDB(user, entitlements, spaces, bundles) {

        if (bundles) {
            await this.umbrellaService.filterAndSaveUmbrellasToDB(bundles, true);
        }
        if (entitlements) {
            await this.productEntitlementsService.saveUserEntitlementsToDB(user, entitlements, true);
        }
        if (spaces) {
            await this.saveSpacesToDB(user, spaces, true);
        }

    }

    // fetch download sizes of ebook products recieved in spaces and classes.
    public async getManifests(spacesData) {

        if (spacesData && spacesData.spaces) {
            let productMap = {};
            let options: any = {
                "books": []
            };

            if (spacesData.spaces) {
                spacesData.spaces.forEach(org => {
                    if (org.classes) {
                        org.classes.forEach(bundleInfo => {
                            if (bundleInfo.bundles) {
                                bundleInfo.bundles.forEach(productInfo => {
                                    if (productInfo.products) {
                                        productInfo.products.forEach((product) => {
                                            if (product['ext-product'].type == 'imgbook') {
                                                if (!productMap[product['dls-product'].code]) {
                                                    productMap[product['dls-product'].code] = {
                                                        "bundleCode": productInfo['bundle-code'],
                                                        "products": [product]
                                                    };
                                                } else {
                                                    productMap[product['dls-product'].code]['products'].push(product)
                                                }
                                            }
                                        });
                                    }
                                });
                            }
                        })
                    }
                });
            }
            if (productMap && Object.keys(productMap).length) {
                Object.keys(productMap).forEach((bookCode) => {
                    options.books.push({
                        "bookCode": bookCode,
                        "bundleCode": productMap[bookCode].bundleCode
                    });
                });

                try {
                    let data = await this.ebookHelperService.fetchManifests(options);

                    Object.keys(productMap).forEach((productcode) => {
                        if (data && data[productcode] && data[productcode].manifest && data[productcode].manifest.shared) {

                            let linkedPEs = data[productcode].linkedPEs;
                            let loManifests = data[productcode].loManifest;
                            let hasValidPEManifests = false;

                            if ((!linkedPEs && !loManifests) || (linkedPEs && loManifests && linkedPEs.length && Object.keys(loManifests).length == linkedPEs.length)) {
                                hasValidPEManifests = true;
                            }

                            let manifest = data[productcode].manifest;


                            let { fileSize, version, fileSizeUnit, unZippedFileSize, unZippedFileSizeUnit } = this.ebookHelperService.calculateManifestSize(manifest, loManifests);

                            // DB uupdate required ONLY for two conditions  - manifest node is not present (fallback), version is outdated (i.e., a new published product is present)
                            //The manifest should consist of unzip size 

                            if (manifest.shared.fileSize.unzip) {
                                productMap[productcode].products.forEach(product => {
                                    product.manifest = { fileSize, version, fileSizeUnit, unZippedFileSize, unZippedFileSizeUnit, hasValidPEManifests };
                                });
                            }else{
                                // if the manifest call does not have unzip size
                                productMap[productcode].products.forEach(product => {
                                    product['manifestCallFail'] = true;
                                });
                            }
                        }else{
                            // checking if the manifest call failed
                            productMap[productcode].products.forEach(product => {
                                product['manifestCallFail'] = true;
                            });
                        }
                    });

                    if (data) {
                        let modeledData = [];
                        Object.keys(data).forEach(key => {
                            modeledData.push({ 'ebookCode': key, 'data': data[key] })
                        });
                        this.downloadHelperService.updateBookAndPEMap(modeledData);
                        this.downloadHelperService.initiateAutoUpdateProcess(modeledData);
                    }

                } catch (err) {
                    console.log("Error while fetching manifests", err);
                }

            }

            // unset product map to save space in case the references are not removed.
            productMap = undefined;
        }
    }

    // function to transform the spaces data as required in desktop app.
    private modelSpacesData(spaces) {
        let user = this.userdataService.getLoggedInUser();
        let spacesData = [];
        this.noUserClassButUserSpace = false;
        spaces && spaces.spaces && spaces.spaces.entities.forEach(space => {
            this.downloadHelperService.bookIDtoSpaceIDmap.set('firstSpace', spaces.spaces.entities[0].space_key);
            if(space.space_type=="student-private"){
                this.downloadHelperService.bookIDtoSpaceIDmap.set('privateSpace', space.space_key);
            }
            let spaceClasses = space.classes && space.classes.count && space.classes.entities.reduce((transformedClassData, classData) => {
                let classesTransformedData = this.modelClassData(classData, space);
                if (classesTransformedData) {
                    transformedClassData.push(classesTransformedData);
                }
                return transformedClassData;
            }, []);

            //If the user has classes in that space, then only insert that space-{class-{bundle-{products}}} data
            if (spaceClasses && spaceClasses.length) {
                let spaceObject = { ...space };
                spaceObject.classes = spaceClasses;
                spacesData.push(spaceObject);
            }else{
                this.noUserClassButUserSpace = true;
            }
        });

        //If any space-{class-{bundle-{product(ebook)}}} data exist then save to DB
        if (spacesData && spacesData.length) {
            let data = {
                uid: user.uid,
                spaces: spacesData
            }

            return data;
        }
    }

    // function to transform the classes data in space as required in desktop app.
    private modelClassData(classData, space) {

        //Get Ebook Products for class
        let classBundlesAndEbookData = this.modelBundlesData(classData, space);

        //If the class has ebook Product, then only insert that class-{bundle-{product(ebook)}} data
        if (classBundlesAndEbookData.length) {
            let transformedClassData = { ...classData };
            transformedClassData.bundles = classBundlesAndEbookData;
            return transformedClassData;
        } else {
            return null;
        }

    }

    // function to transform the bundles data in class as required in desktop app.
    private modelBundlesData(classData, space) {

        let classBundlesAndEbookData = [];
        let bundleCodes = classData.products[0] && classData.products[0]['bundle-codes'];

        //Get all EBook Product Codes from Bundles of class
        bundleCodes && bundleCodes.forEach(code => {

            // get user bundle from class if present
            let bundleData = classData && classData.bundles && classData.bundles[code];

            // if the bundle is not found then it is not a valid bundle. Hence, do not add the class with invalid bundle.
            if (!bundleData) {
                return;
            }

            // Use a shallow copy of the bundle as any changes done to it in umbrella service might not be required here.
            bundleData = JSON.parse(JSON.stringify(bundleData));
            let containsExpiredNonDownloadedPPlusBook = false;
            let bundleProducts = bundleData.products.reduce((transformedProductData, product) => {
                // add products that are ebooks, visible to user and activated(if gated)
                if (product["ext-product"].type == "imgbook" && this.helperService.isProductActive(product) && this.helperService.isEbookVisible(product, space.space_key, true)) {
                    // add the product to bundle if it is not expired or, is expired but downloaded.
                    if (!product.isExpired || (product.isExpired && this.downloadHelperService.getDownloadedInfoForUser(product['dls-product'].code))) {
                        transformedProductData.push(product);
                    } else if (product.isExpired && this.downloadHelperService.getPausedInfoForUser(product['dls-product'].code)) {
                        this.downloadHelperService.removeDownload({
                            productCode: product['dls-product'].code,
                            status: 'inProcess',
                            product: product
                        });
                    }

                    if(product && product.isExpired && !this.downloadHelperService.getDownloadedInfoForUser(product['dls-product'].code) && product.meta && (product.meta['cup-pplus-component'] || product.meta['pplus-component'])) {
                        containsExpiredNonDownloadedPPlusBook = true;
                    }

                }

                this.downloadHelperService.bookIDtoSpaceIDmap.set(product['dls-product'].code, space.space_key);

                return transformedProductData;
            }, []);


            // filter out pplus components if even 1 book is expired.
            if (containsExpiredNonDownloadedPPlusBook) {

                bundleProducts = bundleProducts.filter(book => {
                    let isPPlusComponent = book.meta && (book.meta['cup-pplus-component'] || book.meta['pplus-component']);
                    return isPPlusComponent != true;
                });

            }

            let containsValidEbook = false;

            bundleProducts.forEach(ebook => {
                if (ebook["ext-product"].type == "imgbook") {
                    containsValidEbook = true;
                }
            })

            //If the bundle has ebook Product, then only insert that bundle-{product(ebook)} data
            if (bundleProducts && bundleProducts.length && containsValidEbook) {

                let transformedBundleData = { ...bundleData };
                transformedBundleData.products = bundleProducts;
                transformedBundleData.imageURL = this.helperService.fetchBundleImage(bundleData);
                classBundlesAndEbookData.push(transformedBundleData);

            }
        });
        return classBundlesAndEbookData;
    }

    // set user entitlements in service.
    modifyEntitlementsAndActivateProducts(entitlements) {
        const activatedProducts = [];
        const expiredEntitlements = [];
        const entitlementToExpireDate = {};
        if (entitlements && entitlements.length) {
            entitlements.forEach((product) => {
                activatedProducts.push(product.productcode);
                entitlementToExpireDate[product.productcode] =  product.entitlement.end_date;
                if (Date.now() > product.entitlement.end_date) {
                    expiredEntitlements.push(product.productcode)
                }
            });
        }
        this.activateComponentService.setEntitlementsForUser(activatedProducts);
        this.activateComponentService.setExpiredEntitlementsForUser(expiredEntitlements, entitlementToExpireDate);
    }

    // Save spaces to DB.
    // Store the spaces in local memory.
    private async saveSpacesToDB(user, spaces, updateDB = false) {
        let dbData = {
            uid: user.uid,
            spaces: spaces
        }
        this.desktopDatabaseHelperService.setOriginalSpacesData(dbData, updateDB);
    }

    // get saved Spaces from DB.
    public async fetchSavedSpacesFromDB(user) {
        let spaces = await this.desktopDatabaseHelperService.getOriginalSpacesData();
        return spaces;
    }

    // get spaces from DB, modify them and return.
    public transformSpacesData(spacesData, getManifests = false) {
        let modeledData = this.modelSpacesData(spacesData);
        this.spaceList = modeledData ? modeledData.spaces : [];
        if(getManifests) {
            this.getManifests(modeledData);
        }

        return this.spaceList;
    }

    // get userSpaces saved in memory in service. 
    public async getUserSpacesData(getOriginal = false) {
        if (getOriginal) {
            let spaces = await this.desktopDatabaseHelperService.getOriginalSpacesData();
            return spaces;
        } else {
            return this.spaceList;
        }
    }

    // reset the spaces saved in memory.
    public resetLocalCache(resetOriginal = true) {
        if (resetOriginal) {
            this.desktopDatabaseHelperService.resetOriginalData(this.desktopDatabaseHelperService.DATAKEYS.SPACES);
        }
        this.spaceList = undefined;
    }

}